import { themeVariables } from "Components/theme";
import chroma from "chroma-js";
import compact from "lodash/compact";
import filter from "lodash/filter";
import flatMap from "lodash/flatMap";
import includes from "lodash/includes";
import intersection from "lodash/intersection";
import map from "lodash/map";
import Feature from "ol/Feature";
import { Point } from "ol/geom";
import React, { memo, useCallback, useEffect, useRef } from "react";
import { RLayerCluster, RStyle } from "rlayers";
import { RCircle, RFill, RIcon, RStroke, RText } from "rlayers/style";
import { SIZES } from "../map.styled";
import getIsInTimelineInterval from "../utils/getIsInTimelineInterval";
import { MAX_CLUSTERED_FEATURES_DEFINED, clusterDistance, getRadius } from "../utils/map.utils";
import { iconBg } from "../utils/styles";
const rFont = `normal normal normal ${themeVariables["font-size-base"]}/1 ${themeVariables["font-family-base"]}`;
const icomoonFont = `normal normal normal ${SIZES.ICOMOON_FONT_SIZE}px/1 ${themeVariables["font-family-icomoon"]}`;
const getClusteredFeatureData = (features) => compact(map(flatMap(features, f => f.get("features")), feature => {
    var _a;
    return feature && ((_a = feature.get("data")) === null || _a === void 0 ? void 0 : _a.id)
        ? feature.get("data")
        : undefined;
}));
function PointsCluster(props) {
    const initialRef = useRef(null);
    const rLayerClusterRef = props.layerRef || initialRef;
    const selectedRef = useRef(undefined);
    const firstTimeRendered = useRef(false);
    const timelineIntervalRef = useRef(props.timelineInterval);
    useEffect(() => {
        var _a;
        selectedRef.current = props.selected || [];
        firstTimeRendered.current && ((_a = rLayerClusterRef.current) === null || _a === void 0 ? void 0 : _a.cluster.changed());
    }, [props.selected]);
    useEffect(() => {
        var _a;
        timelineIntervalRef.current = props.timelineInterval;
        firstTimeRendered.current && ((_a = rLayerClusterRef.current) === null || _a === void 0 ? void 0 : _a.cluster.changed());
    }, [props.timelineInterval]);
    const cacheId = useCallback((feature) => {
        const data = getClusteredFeatureData([feature]);
        const dataIds = map(data, "id");
        const dataSelected = intersection(selectedRef.current, dataIds).length > 0;
        const isInTimelineInterval = getIsInTimelineInterval(data, timelineIntervalRef.current);
        return `${dataSelected}_${data.length === 1 ? data[0].color : Math.min(data.length, MAX_CLUSTERED_FEATURES_DEFINED)}_${isInTimelineInterval}`;
    }, []);
    const clusterRender = useCallback((feature) => {
        const data = getClusteredFeatureData([feature]);
        if (!data.length)
            return React.createElement(React.Fragment, null);
        const dataIds = map(data, "id");
        const dataSelected = intersection(selectedRef.current, dataIds).length > 0;
        const isInTimelineInterval = getIsInTimelineInterval(data, timelineIntervalRef.current);
        if (data.length === 1) {
            const fillColor = dataSelected
                ? themeVariables["brand-primary-500"]
                : data[0].color || themeVariables["gray-550"];
            return (React.createElement(React.Fragment, null,
                React.createElement(RIcon, { src: iconBg, color: "#ffffff", anchor: SIZES.ICON_ANCHOR, opacity: isInTimelineInterval ? 1 : dataSelected ? 0.4 : 0.5 }),
                React.createElement(RText, { offsetY: SIZES.ICOMOON_TEXT_OFFSET_Y, text: "\uE93C", font: icomoonFont, textAlign: "center" },
                    React.createElement(RFill, { color: isInTimelineInterval ? fillColor : chroma(fillColor).alpha(0.35).css() }))));
        }
        const radius = getRadius(data.length);
        const fillColor = themeVariables["brand-primary-500"];
        const strokeColor = dataSelected ? themeVariables["brand-primary-500"] : "#ffffff";
        return (React.createElement(React.Fragment, null,
            React.createElement(RCircle, { radius: radius, opacity: 0.9 },
                React.createElement(RFill, { color: isInTimelineInterval ? fillColor : chroma(fillColor).alpha(0.4).css() }),
                React.createElement(RStroke, { color: isInTimelineInterval ? strokeColor : chroma(strokeColor).alpha(0.5).css(), width: 2 })),
            React.createElement(RText, { text: data.length > MAX_CLUSTERED_FEATURES_DEFINED
                    ? `${MAX_CLUSTERED_FEATURES_DEFINED}+`
                    : data.length.toString(), font: rFont, textAlign: "center" },
                React.createElement(RFill, { color: isInTimelineInterval ? "#fff" : chroma("#fff").alpha(0.6).css() }))));
    }, [selectedRef.current]);
    useEffect(() => {
        if (rLayerClusterRef.current) {
            const features = map(filter(props.data, e => !includes(props.deleted, e.id)), d => new Feature({
                geometry: new Point(d.coordinates[0]),
                data: d
            }));
            rLayerClusterRef.current.cluster.clear();
            features.length && rLayerClusterRef.current.cluster.addFeatures(features);
            firstTimeRendered.current = true;
        }
    }, [props.data, props.deleted]);
    const handlePointerEnterOrLeave = useCallback((e) => {
        if (!e.target || !e.pixel || !props.onPointerEnterOrLeave)
            return;
        const data = getClusteredFeatureData([e.target]);
        const dataIds = map(data, "id");
        if (!dataIds.length)
            return;
        const isInTimelineInterval = getIsInTimelineInterval(data, timelineIntervalRef.current);
        if (!isInTimelineInterval)
            return;
        props.onPointerEnterOrLeave(e.type, e.pixel, dataIds);
    }, [props.onPointerEnterOrLeave]);
    return (React.createElement(RLayerCluster, { ref: rLayerClusterRef, distance: clusterDistance, onPointerEnter: props.onPointerEnterOrLeave ? handlePointerEnterOrLeave : undefined, onPointerLeave: props.onPointerEnterOrLeave ? handlePointerEnterOrLeave : undefined },
        React.createElement(RStyle.RStyle, { cacheSize: 1024, cacheId: cacheId, render: clusterRender })));
}
export default memo(PointsCluster);
